import { useEffect, useState, useCallback } from "react";
import {
  Button, Card, Table, Spinner, Badge, Modal, Form, Row, Col,
} from "react-bootstrap";
import { FaPlus, FaPen, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { fetchAccountingPermissions, ACCOUNTING_CONFIG_BANK } from "../../libs/customerCreatePermission";
import "./accountingResponsive.css";
import { formatVND as fmtVND, toMoneyNumber } from "./utils/moneyFormat";
import MoneyInput from "./components/MoneyInput";

const ACCOUNT_TYPES = [
  { value: "BANK", label: "Ngân hàng" },
  { value: "CASH", label: "Tiền mặt" },
];

const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const INITIAL_FORM = {
  bankName: "",
  accountNumber: "",
  accountHolder: "",
  accountType: "BANK",
  openingBalance: "",
};

const BankAccountList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [editId, setEditId] = useState(null);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_CONFIG_BANK);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/accounting/bank-accounts");
      const body = unwrap(res);
      const data = Array.isArray(body) ? body : body?.content || [];
      setRows(data);
    } catch (err) {
      toast.error("Không thể tải danh sách tài khoản ngân hàng");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(INITIAL_FORM); setEditId(null); setShowModal(true); };
  const openEdit = (row) => {
    setForm({
      bankName: row.bankName || "",
      accountNumber: row.accountNumber || "",
      accountHolder: row.accountHolder || "",
      accountType: row.accountType || "BANK",
      openingBalance: row.openingBalance ?? "",
      currentBalance: row.balance ?? row.currentBalance ?? 0,
    });
    setEditId(row.id);
    setShowModal(true);
  };

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankName || !form.accountNumber || !form.accountHolder) {
      toast.warn("Vui lòng điền đầy đủ thông tin bắt buộc");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, openingBalance: toMoneyNumber(form.openingBalance) };
      if (editId) {
        await axiosInstance.put(`/api/accounting/bank-accounts/${editId}`, payload);
        toast.success("Cập nhật tài khoản thành công");
      } else {
        await axiosInstance.post("/api/accounting/bank-accounts", payload);
        toast.success("Thêm tài khoản thành công");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Lưu tài khoản thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Xoá tài khoản?",
      text: "Dữ liệu sẽ bị xoá vĩnh viễn.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/api/accounting/bank-accounts/${id}`);
      toast.success("Đã xoá tài khoản");
      fetchData();
    } catch (err) {
      toast.error("Xoá thất bại");
    }
  };

  const body = (
      <div className={embedded ? "" : "acc-page"}>
        <Card style={cardStyle}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              {!embedded && <h5 className="mb-0 fw-bold">Quản lý Tài khoản Ngân hàng</h5>}
              {embedded && <h6 className="mb-0 fw-bold">Tài khoản ngân hàng</h6>}
              {perm.canCreate && (
                <Button variant="primary" size="sm" onClick={openCreate}>
                  <FaPlus className="me-1" /> Thêm tài khoản
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">Chưa có tài khoản nào</div>
            ) : (
              <div className="acc-table-wrap">
              <Table striped hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Tên NH</th>
                    <th>Số TK</th>
                    <th>Chủ TK</th>
                    <th>Loại</th>
                    <th className="text-end">Số dư hiện tại</th>
                    <th className="text-center">Trạng thái</th>
                    <th className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="fw-semibold">{r.bankName}</td>
                      <td>{r.accountNumber}</td>
                      <td>{r.accountHolder}</td>
                      <td>
                        <Badge bg={r.accountType === "CASH" ? "info" : "primary"}>
                          {ACCOUNT_TYPES.find((t) => t.value === r.accountType)?.label || r.accountType}
                        </Badge>
                      </td>
                      <td className="text-end fw-semibold">{fmtVND(r.balance ?? r.currentBalance ?? 0)}</td>
                      <td className="text-center">
                        <Badge bg={r.active !== false ? "success" : "secondary"}>
                          {r.active !== false ? "Hoạt động" : "Ngừng"}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          {perm.canEdit && (
                            <Button variant="outline-primary" size="sm" title="Sửa" onClick={() => openEdit(r)}>
                              <FaPen />
                            </Button>
                          )}
                          {perm.canDelete && (
                            <Button variant="outline-danger" size="sm" title="Xoá" onClick={() => handleDelete(r.id)}>
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Sửa tài khoản" : "Thêm tài khoản"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Tên ngân hàng <span className="text-danger">*</span></Form.Label>
                    <Form.Control value={form.bankName} onChange={(e) => onFormChange("bankName", e.target.value)} placeholder="VD: Vietcombank" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Số tài khoản <span className="text-danger">*</span></Form.Label>
                    <Form.Control value={form.accountNumber} onChange={(e) => onFormChange("accountNumber", e.target.value)} placeholder="Số tài khoản" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Chủ tài khoản <span className="text-danger">*</span></Form.Label>
                    <Form.Control value={form.accountHolder} onChange={(e) => onFormChange("accountHolder", e.target.value)} placeholder="Tên chủ TK" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Loại tài khoản</Form.Label>
                    <Form.Select value={form.accountType} onChange={(e) => onFormChange("accountType", e.target.value)}>
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>{editId ? "Số dư hiện tại" : "Số dư ban đầu"}</Form.Label>
                    {editId ? (
                      <Form.Control value={fmtVND(form.currentBalance)} readOnly plaintext className="fw-semibold" />
                    ) : (
                      <MoneyInput value={form.openingBalance} onValueChange={(v) => onFormChange("openingBalance", v)} placeholder="0" />
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>Đóng</Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : editId ? "Cập nhật" : "Thêm mới"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default BankAccountList;
